<template>
  <div class="webhooks-settings">
    <collapsible-section
      title="Webhooks"
      icon="tim-icons icon-spaceship"
      :badge="endpoints.length || ''"
      :collapsible="false"
    >
      <info-hint variant="primary" style="width: 100%" class="mb-3">
        Each request is signed with the endpoint's secret in the <strong>X-OTC-Signature</strong> header so you can verify it came from us. We retry failed deliveries with backoff and disable an endpoint that keeps failing. Only <strong>https</strong> URLs that resolve to a public address are accepted.
      </info-hint>

      <!-- Loading -->
      <div v-if="loading" class="webhooks-loading">
        <i class="el-icon-loading"></i> Loading webhooks
      </div>

      <!-- Empty -->
      <div v-else-if="!endpoints.length" class="webhooks-empty">
        <img src="/img/pandas/webhooks.png" alt="No webhook endpoints yet" class="empty-state-img empty-state-img--sm webhooks-empty__img" />
        <h4 class="webhooks-empty__title text-white">No webhook endpoints yet</h4>
        <p class="webhooks-empty__hint">Add an endpoint and choose which events should reach it.</p>
        <base-button type="primary" size="sm" class="mt-2" v-on:click="openCreate">
          <i class="tim-icons icon-simple-add mr-1"></i> Add endpoint
        </base-button>
      </div>

      <!-- Endpoint list -->
      <template v-else>
        <div class="endpoint-list-head">
          <base-button type="primary" size="sm" v-on:click="openCreate">
            <i class="tim-icons icon-simple-add mr-1"></i> Add endpoint
          </base-button>
        </div>
        <div class="endpoint-list">
      <div v-for="ep in endpoints" :key="ep.id" class="endpoint-card">
        <div class="endpoint-main">
          <div class="endpoint-url-row">
            <span class="status-dot" :class="ep.is_active ? 'is-on' : 'is-off'"></span>
            <span class="endpoint-url" :title="ep.url">{{ ep.url }}</span>
            <i class="tim-icons icon-single-copy-04 copy-url" title="Copy URL" v-on:click="copy(ep.url)"></i>
          </div>
          <p v-if="ep.description" class="endpoint-desc">{{ ep.description }}</p>
          <div class="endpoint-meta">
            <span class="meta-pill">{{ ep.subscriptions.length }} event{{ ep.subscriptions.length === 1 ? '' : 's' }}</span>
            <span v-if="!ep.is_active" class="meta-pill pill-danger">Disabled</span>
            <span v-if="ep.last_delivery_status" class="meta-pill" :class="deliveryPillClass(ep.last_delivery_status)">
              Last: {{ ep.last_delivery_status }}
            </span>
            <span v-if="ep.last_delivery_at" class="meta-time">{{ fmtDate(ep.last_delivery_at) }}, {{ fmtTime(ep.last_delivery_at) }}</span>
          </div>
        </div>
        <div class="endpoint-actions">
          <base-button v-if="!ep.is_active" variant="outlined" tone="success" v-on:click="reenable(ep)">
            <i class="tim-icons icon-button-power"></i>Re-enable
          </base-button>
          <base-button variant="outlined" tone="primary" v-on:click="openDeliveries(ep)">
            <i class="tim-icons icon-bullet-list-67"></i>Deliveries
          </base-button>
          <base-button variant="outlined" tone="primary" v-on:click="openEdit(ep)">
            <i class="tim-icons icon-pencil"></i>Edit
          </base-button>
          <el-tooltip content="Delete endpoint" effect="light" :open-delay="300" placement="top">
            <base-button type="danger" link size="sm" class="btn-delete" v-on:click="confirmDelete(ep)">
              <i class="tim-icons icon-trash-simple"></i>
            </base-button>
          </el-tooltip>
        </div>
      </div>
        </div>
      </template>
    </collapsible-section>

    <!-- Create / edit drawer -->
    <base-drawer :show="showEditor" :title="editing ? 'Edit endpoint' : 'Add endpoint'" @close="onCloseEditor">
      <drawer-section title="Endpoint" icon="icon-world">
        <label>Payload URL</label>
        <base-input v-model="form.url" placeholder="https://your-server.com/webhooks/otc" />
        <small v-if="formErrors.url" class="field-error">{{ formErrors.url }}</small>

        <label class="mt-2">Description <span class="optional">(optional)</span></label>
        <base-input v-model="form.description" placeholder="e.g. Accounting sync" />

        <div class="mt-3">
          <el-switch active-color="#e14eca" v-model="form.is_active" />
          <label class="text-white"> &nbsp; Active
            <el-popover popper-class="otc-pop" trigger="hover" placement="right">
              <div>
                <div class="popover-body">Events are only delivered to this endpoint while it is active.</div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info ml-1"></i>
            </el-popover>
          </label>
        </div>
      </drawer-section>

      <drawer-section title="Events" icon="icon-bell-55">
        <p class="events-help">Choose which events trigger a request to this endpoint.</p>
        <small v-if="formErrors.events" class="field-error d-block mb-2">{{ formErrors.events }}</small>
        <div v-for="(triggers, category) in groupedTriggers" :key="category" class="event-group">
          <div class="event-group-label">{{ categoryLabel(category) }}</div>
          <div class="event-rows">
            <label v-for="t in triggers" :key="t.trigger_type_id" class="event-row" :class="{ checked: form.trigger_type_ids.includes(t.trigger_type_id) }">
              <input type="checkbox" :value="t.trigger_type_id" v-model="form.trigger_type_ids" />
              <span class="event-name">{{ triggerName(t) }}</span>
              <code class="event-key">{{ eventType(t.trigger_key) }}</code>
            </label>
          </div>
        </div>
      </drawer-section>

      <drawer-section v-if="editing" title="Signing secret" icon="icon-key-25">
        <info-hint variant="primary" style="width: 100%" class="mb-3">
          Used to verify the <strong>X-OTC-Signature</strong> header. Rotating it invalidates the old secret immediately.
        </info-hint>
        <div v-if="rotatedSecret" class="credential-field">
          <code>{{ rotatedSecret }}</code>
          <i class="tim-icons icon-single-copy-04" v-on:click="copy(rotatedSecret)" title="Copy"></i>
        </div>
        <div v-if="!rotatedSecret && editing && editing.signing_secret_hint" class="credential-field mb-2">
          <code>{{ editing.signing_secret_hint }}</code>
        </div>
        <base-button v-if="!rotatedSecret" variant="outlined" tone="primary" v-on:click="rotateSecret">
          <i class="tim-icons icon-refresh-02"></i>Rotate secret
        </base-button>
        <p v-if="rotatedSecret" class="secret-note">Copy this now and store it on your server to verify signatures.</p>
      </drawer-section>

      <template #footer>
        <div class="footer-actions">
          <base-button type="secondary" size="sm" v-on:click="onCloseEditor">Cancel</base-button>
          <base-button type="primary" size="sm" v-on:click="save" :disabled="saving">
            <i v-if="saving" class="el-icon-loading mr-1"></i>{{ editing ? 'Save' : 'Create endpoint' }}
          </base-button>
        </div>
      </template>
    </base-drawer>

    <!-- New-secret reveal (after create) -->
    <base-drawer :show="showSecret" title="Endpoint created" @close="showSecret = false">
      <drawer-section title="Signing secret" icon="icon-key-25">
        <info-hint variant="warning" style="width: 100%" class="mb-3">
          Copy your signing secret now. For your security we won't show it in full again, though you can rotate it later.
        </info-hint>
        <div class="credential-field">
          <code>{{ newSecret }}</code>
          <i class="tim-icons icon-single-copy-04" v-on:click="copy(newSecret)" title="Copy"></i>
        </div>
      </drawer-section>
      <template #footer>
        <div class="footer-actions">
          <base-button type="primary" size="sm" v-on:click="showSecret = false">Done</base-button>
        </div>
      </template>
    </base-drawer>

    <!-- Deliveries drawer -->
    <base-drawer :show="showDeliveries" title="Recent deliveries" @close="showDeliveries = false">
      <div v-if="deliveriesFor" class="endpoint-url deliveries-url mb-3" :title="deliveriesFor.url">{{ deliveriesFor.url }}</div>
      <div v-if="deliveriesLoading" class="webhooks-loading"><i class="el-icon-loading"></i> Loading</div>
        <div v-else-if="!deliveries.length" class="webhooks-empty small">
          <h4 class="webhooks-empty__title text-white">No deliveries yet</h4>
          <p class="webhooks-empty__hint">Attempts will appear here once events fire.</p>
        </div>
        <div v-else class="delivery-list">
          <div v-for="d in deliveries" :key="d.id" class="delivery-row">
            <div class="delivery-top">
              <span class="meta-pill" :class="deliveryPillClass(d.status)">{{ d.status }}</span>
              <code class="delivery-event">{{ eventType(d.trigger_key) }}</code>
              <span class="delivery-code" v-if="d.response_status">HTTP {{ d.response_status }}</span>
            </div>
            <div class="delivery-bottom">
              <span class="delivery-time">{{ fmtDate(d.created_at) }}, {{ fmtTime(d.created_at) }}</span>
              <span class="delivery-attempts">{{ d.attempts }} attempt{{ d.attempts === 1 ? '' : 's' }}</span>
              <span v-if="d.error_message" class="delivery-error" :title="d.error_message">{{ d.error_message }}</span>
              <span class="delivery-redeliver" v-on:click="redeliver(d)">Redeliver</span>
            </div>
          </div>
          <div v-if="deliveriesHasMore" class="delivery-load-more">
            <base-button variant="outlined" tone="primary" :disabled="deliveriesLoadingMore" v-on:click="loadMoreDeliveries">
              <i v-if="deliveriesLoadingMore" class="el-icon-loading"></i>
              Load more
            </base-button>
          </div>
        </div>
    </base-drawer>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '../../api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { Switch, Popover } from 'element-ui'
import swal from 'sweetalert2'
const axios = require('axios')

const CATEGORY_LABELS = {
  booking: 'Bookings',
  transaction: 'Transactions',
  payment: 'Payments',
  game: 'Events',
}

export default {
  name: 'webhooks-settings',
  components: {
    [Switch.name]: Switch,
    [Popover.name]: Popover,
  },
  data() {
    return {
      companyId: localStorage.getItem('companyId'),
      loading: true,
      saving: false,
      endpoints: [],
      availableTriggers: [],
      // editor
      showEditor: false,
      editing: null, // endpoint being edited, or null for create
      form: { url: '', description: '', is_active: true, trigger_type_ids: [] },
      formErrors: { url: '', events: '' },
      rotatedSecret: '',
      // new-secret reveal
      showSecret: false,
      newSecret: '',
      // deliveries
      showDeliveries: false,
      deliveriesFor: null,
      deliveries: [],
      deliveriesLoading: false,
      deliveriesHasMore: false,
      deliveriesLoadingMore: false,
    }
  },
  computed: {
    groupedTriggers() {
      const groups = {}
      for (const t of this.availableTriggers) {
        const c = t.category || 'other'
        if (!groups[c]) groups[c] = []
        groups[c].push(t)
      }
      return groups
    },
  },
  mounted() {
    this.fetchConfig()
  },
  methods: {
    getAuthToken() {
      return new Promise((resolve) => {
        Auth.currentSession()
          .then((data) => resolve(data.idToken.jwtToken))
          .catch((err) => console.log(err))
      })
    },
    fmtDate(d) { return formatGroupDate(d) },
    fmtTime(d) { return formatGroupTime(d) },
    categoryLabel(c) { return CATEGORY_LABELS[c] || (c.charAt(0).toUpperCase() + c.slice(1)) },
    // Display name. Overrides the shared DB trigger name where webhook-facing copy
    // differs (OTC experiences are "events", not "games") — without touching the
    // name email automation shows.
    triggerName(t) {
      const overrides = { game_completed: 'Event completed' }
      return overrides[t.trigger_key] || t.name
    },
    // Coarse public event name, derived from the trigger key for display.
    eventType(triggerKey) {
      const map = {
        booking_created_customer: 'booking.created',
        booking_created_internal: 'booking.created',
        booking_cancelled: 'booking.cancelled',
        booking_reactivated: 'booking.reactivated',
        booking_datetime_updated: 'booking.datetime_updated',
        booking_group_size_updated: 'booking.group_size_updated',
        booking_rescheduled_slot: 'booking.rescheduled',
        transaction_created: 'transaction.created',
        transaction_cancelled: 'transaction.cancelled',
        payment_success: 'payment.succeeded',
        payment_refund: 'payment.refunded',
        game_completed: 'event.completed',
      }
      return map[triggerKey] || triggerKey
    },
    deliveryPillClass(status) {
      if (status === 'success') return 'pill-success'
      if (status === 'failed') return 'pill-warning'
      if (status === 'exhausted') return 'pill-danger'
      return 'pill-neutral'
    },
    copy(text) {
      navigator.clipboard.writeText(text)
      this.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
    },
    notifyError(message) {
      this.$notify({ type: 'danger', message: message || 'Something went wrong', icon: 'tim-icons icon-alert-circle-exc' })
    },

    async fetchConfig() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks'
        const res = await axios.get(url, { headers: { Authorization: token } })
        this.endpoints = res.data.endpoints || []
        this.availableTriggers = res.data.available_triggers || []
      } catch (err) {
        console.log(err)
        this.notifyError('Could not load webhooks')
      } finally {
        this.loading = false
      }
    },

    openCreate() {
      this.editing = null
      this.form = { url: '', description: '', is_active: true, trigger_type_ids: [] }
      this.formErrors = { url: '', events: '' }
      this.rotatedSecret = ''
      this.showEditor = true
    },
    openEdit(ep) {
      this.editing = ep
      this.form = {
        url: ep.url,
        description: ep.description || '',
        is_active: !!ep.is_active,
        trigger_type_ids: ep.subscriptions.map((s) => s.trigger_type_id),
      }
      this.formErrors = { url: '', events: '' }
      this.rotatedSecret = ''
      this.showEditor = true
    },
    onCloseEditor() {
      this.showEditor = false
    },

    validate() {
      this.formErrors = { url: '', events: '' }
      let ok = true
      const u = (this.form.url || '').trim()
      if (!u) { this.formErrors.url = 'A URL is required'; ok = false }
      else if (!/^https:\/\//i.test(u)) { this.formErrors.url = 'URL must start with https://'; ok = false }
      if (!this.form.trigger_type_ids.length) { this.formErrors.events = 'Select at least one event'; ok = false }
      return ok
    },

    async save() {
      if (!this.validate()) return
      this.saving = true
      try {
        const token = await this.getAuthToken()
        const base = API_LOCATION + 'companies/' + this.companyId + '/webhooks'
        const payload = {
          url: this.form.url.trim(),
          description: this.form.description.trim(),
          is_active: this.form.is_active,
          trigger_type_ids: this.form.trigger_type_ids,
        }
        if (this.editing) {
          await axios.put(base + '/' + this.editing.id, payload, { headers: { Authorization: token } })
          this.$notify({ type: 'success', message: 'Webhook updated', icon: 'tim-icons icon-check-2' })
        } else {
          const res = await axios.post(base, payload, { headers: { Authorization: token } })
          this.newSecret = res.data.signing_secret
          this.showSecret = true
          this.$notify({ type: 'success', message: 'Webhook created', icon: 'tim-icons icon-check-2' })
        }
        this.showEditor = false
        await this.fetchConfig()
      } catch (err) {
        this.handleAxiosError(err)
      } finally {
        this.saving = false
      }
    },

    handleAxiosError(err) {
      console.log(err)
      let message = 'Something went wrong'
      try {
        const data = err.response && err.response.data
        const parsed = typeof data === 'string' ? JSON.parse(data) : data
        if (parsed && parsed.validation_errors && parsed.validation_errors.length) {
          const ve = parsed.validation_errors[0]
          if (ve.field === 'url') this.formErrors.url = ve.message
          if (ve.field === 'trigger_type_ids') this.formErrors.events = ve.message
          message = parsed.error || message
        } else if (parsed && parsed.error) {
          message = parsed.error
        }
      } catch (_) { /* fall back to generic */ }
      this.notifyError(message)
    },

    async rotateSecret() {
      if (!this.editing) return
      try {
        const token = await this.getAuthToken()
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks/' + this.editing.id
        const res = await axios.put(url, { rotate_secret: true }, { headers: { Authorization: token } })
        this.rotatedSecret = res.data.signing_secret
      } catch (err) {
        this.handleAxiosError(err)
      }
    },

    confirmDelete(ep) {
      swal({
        title: 'Delete this endpoint?',
        text: 'It will stop receiving events and its delivery history will be removed.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
      }).then((response) => {
        if (response.value === true) this.remove(ep)
      })
    },
    async reenable(ep) {
      try {
        const token = await this.getAuthToken()
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks/' + ep.id
        await axios.put(url, { is_active: true }, { headers: { Authorization: token } })
        this.$notify({ type: 'success', message: 'Endpoint re-enabled', icon: 'tim-icons icon-check-2' })
        await this.fetchConfig()
      } catch (err) {
        this.handleAxiosError(err)
      }
    },
    async remove(ep) {
      try {
        const token = await this.getAuthToken()
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks/' + ep.id
        await axios.delete(url, { headers: { Authorization: token } })
        this.$notify({ type: 'success', message: 'Webhook deleted', icon: 'tim-icons icon-check-2' })
        await this.fetchConfig()
      } catch (err) {
        this.handleAxiosError(err)
      }
    },

    async openDeliveries(ep) {
      this.deliveriesFor = ep
      this.deliveries = []
      this.deliveriesHasMore = false
      this.showDeliveries = true
      this.deliveriesLoading = true
      try {
        const token = await this.getAuthToken()
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks/' + ep.id + '/deliveries?limit=25'
        const res = await axios.get(url, { headers: { Authorization: token } })
        this.deliveries = res.data.deliveries || []
        this.deliveriesHasMore = !!res.data.has_more
      } catch (err) {
        this.handleAxiosError(err)
      } finally {
        this.deliveriesLoading = false
      }
    },
    async loadMoreDeliveries() {
      if (!this.deliveries.length || this.deliveriesLoadingMore) return
      this.deliveriesLoadingMore = true
      try {
        const token = await this.getAuthToken()
        const beforeId = this.deliveries[this.deliveries.length - 1].id
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks/' + this.deliveriesFor.id + '/deliveries?limit=25&before_id=' + beforeId
        const res = await axios.get(url, { headers: { Authorization: token } })
        this.deliveries = this.deliveries.concat(res.data.deliveries || [])
        this.deliveriesHasMore = !!res.data.has_more
      } catch (err) {
        this.handleAxiosError(err)
      } finally {
        this.deliveriesLoadingMore = false
      }
    },
    async redeliver(d) {
      try {
        const token = await this.getAuthToken()
        const url = API_LOCATION + 'companies/' + this.companyId + '/webhooks/' + this.deliveriesFor.id + '/deliveries/' + d.id + '/redeliver'
        const res = await axios.post(url, {}, { headers: { Authorization: token } })
        if (res.data.success) {
          this.$notify({ type: 'success', message: 'Redelivered', icon: 'tim-icons icon-check-2' })
        } else {
          this.notifyError('Delivery failed: ' + (res.data.error || 'unknown error'))
        }
        await this.openDeliveries(this.deliveriesFor)
      } catch (err) {
        this.handleAxiosError(err)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.webhooks-settings { margin-top: 0.5rem; }

.webhooks-loading { color: #e14eca; font-size: 0.85rem; padding: 0.5rem 0; }
.webhooks-loading .el-icon-loading { margin-right: 0.35rem; }

.endpoint-list-head { display: flex; justify-content: flex-start; margin-bottom: 0.75rem; }

.webhooks-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem 1rem 1rem;
  &.small { padding: 1.25rem 1rem; }
}
.webhooks-empty__img { width: 96px; height: 96px; margin-bottom: 0.75rem; }
.webhooks-empty__title { font-size: 1rem; font-weight: 600; margin: 0 0 0.2rem; }
.webhooks-empty__hint { font-size: 0.82rem; color: rgba(255, 255, 255, 0.65); margin: 0 0 0.9rem; }

.endpoint-list { display: flex; flex-direction: column; gap: 0.75rem; }
.endpoint-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  background: rgba(30, 30, 47, 0.6);
  border: 1px solid rgba(125, 125, 125, 0.22);
  border-radius: 0.5rem;
}
.endpoint-main { min-width: 0; flex: 1; }
.endpoint-url-row { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
.status-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  &.is-on { background: #00f2c3; box-shadow: 0 0 0 3px rgba(0, 242, 195, 0.15); }
  &.is-off { background: #fd5d93; box-shadow: 0 0 0 3px rgba(253, 93, 147, 0.15); }
}
.endpoint-url {
  font-family: ui-monospace, monospace;
  font-size: 0.85rem; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.copy-url {
  cursor: pointer;
  color: rgba(125, 125, 125, 0.9);
  font-size: 0.8rem;
  flex-shrink: 0;
  transition: color 0.15s ease;
  &:hover { color: #e14eca; }
}
.endpoint-desc { margin: 0.35rem 0 0; font-size: 0.78rem; color: rgba(255, 255, 255, 0.55); }
.endpoint-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.5rem; }
.meta-time { font-size: 0.72rem; color: rgba(255, 255, 255, 0.4); }

.meta-pill {
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(125, 125, 125, 0.18);
  color: rgba(255, 255, 255, 0.75);
  text-transform: capitalize;
}
.pill-success { background: rgba(0, 242, 195, 0.15); color: #00f2c3; }
.pill-warning { background: rgba(255, 159, 64, 0.15); color: #ff9f40; }
.pill-danger { background: rgba(253, 93, 147, 0.15); color: #fd5d93; }
.pill-neutral { background: rgba(125, 125, 125, 0.18); color: rgba(255, 255, 255, 0.7); }

.endpoint-actions { display: flex; align-items: center; gap: 0.4rem; flex-shrink: 0; }

.optional { color: rgba(255, 255, 255, 0.45); font-weight: 400; }
.field-error { color: #fd5d93; font-size: 0.75rem; }
.events-help { font-size: 0.82rem; color: rgba(255, 255, 255, 0.7); margin-bottom: 0.75rem; }

.event-group { margin-bottom: 0.85rem; }
.event-group-label {
  font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em;
  color: #e14eca; font-weight: 600; margin-bottom: 0.25rem;
}
.event-rows { display: flex; flex-direction: column; }
.event-row {
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.5rem 0.55rem; border-radius: 0.35rem; cursor: pointer; margin: 0;
  transition: background 0.12s ease;
  // No checked/resting background in either mode — the checkbox carries the
  // state; only a neutral hover highlight.
  &:hover { background: rgba(125, 125, 125, 0.08); }
  input { accent-color: #e14eca; cursor: pointer; width: 15px; height: 15px; flex-shrink: 0; }
}
.event-name { font-size: 0.85rem; color: #fff; flex: 1; }
.event-key {
  font-family: ui-monospace, monospace; font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4); background: rgba(125, 125, 125, 0.12);
  padding: 2px 6px; border-radius: 4px;
}

/* Credential boxes use the global .credential-field (dashboard/custom/_credential-field.scss). */
.secret-note { font-size: 0.75rem; color: rgba(255, 159, 64, 0.85); margin-top: 0.4rem; }

.delivery-list { display: flex; flex-direction: column; gap: 0.55rem; }
.delivery-row {
  padding: 0.6rem 0.75rem; border-radius: 0.4rem;
  background: rgba(30, 30, 47, 0.5); border: 1px solid rgba(125, 125, 125, 0.2);
}
.delivery-top { display: flex; align-items: center; gap: 0.5rem; }
.delivery-event { font-family: ui-monospace, monospace; font-size: 0.75rem; color: rgba(255, 255, 255, 0.75); }
.delivery-code { font-size: 0.72rem; color: rgba(255, 255, 255, 0.5); margin-left: auto; }
.delivery-bottom { display: flex; align-items: center; flex-wrap: wrap; gap: 0.6rem; margin-top: 0.4rem; }
.delivery-time, .delivery-attempts { font-size: 0.72rem; color: rgba(255, 255, 255, 0.45); }
.delivery-error { font-size: 0.72rem; color: #fd5d93; flex-basis: 100%; word-break: break-word; }
.delivery-redeliver { font-size: 0.72rem; color: #e14eca; cursor: pointer; text-decoration: underline; margin-left: auto; }
.delivery-load-more { display: flex; justify-content: center; padding-top: 0.5rem; }
.deliveries-url { color: rgba(255, 255, 255, 0.55); font-size: 0.78rem; }

.footer-actions { display: flex; justify-content: flex-end; gap: 0.6rem; }

/* Mobile: stack the endpoint card so the URL + actions don't overflow */
@media (max-width: 575px) {
  .endpoint-card { flex-direction: column; align-items: stretch; gap: 0.75rem; }
  .endpoint-actions { width: 100%; flex-wrap: wrap; }
  .endpoint-actions > * { flex: 1; }
  .btn-delete { flex: 0 0 auto; }
}

/* Light mode parity (.white-content is toggled on an ancestor) */
.white-content .webhooks-settings {
  .endpoint-url, .event-name,
  .delivery-event, .webhooks-empty__title { color: #1d253b; }

  .endpoint-desc, .events-help, .optional, .webhooks-empty__hint,
  .delivery-code, .delivery-time, .delivery-attempts, .meta-time,
  .event-key, .deliveries-url { color: rgba(0, 0, 0, 0.55); }

  .endpoint-card, .delivery-row { background: #fff; border-color: rgba(0, 0, 0, 0.1); }
  .event-row:hover { background: rgba(0, 0, 0, 0.04); }

  .meta-pill, .pill-neutral { color: rgba(0, 0, 0, 0.65); }
  .event-key { background: rgba(0, 0, 0, 0.06); }
}
</style>
